/**
 * The app's loading mark.
 *
 * WHAT THIS REPLACES. A bare MUI `CircularProgress` — the default Material
 * spinner, on a product that has a palette, a type scale and a card language of
 * its own everywhere else. It reads as unfinished because it IS unfinished: it
 * is the component you get for not choosing one. "This looks very basic to me"
 * was the note, and it was right.
 *
 * WHAT IT IS INSTEAD. Two concentric arcs turning at different speeds and in
 * opposite directions, over a soft halo of the accent. The counter-rotation is
 * what makes it read as a made object rather than a default: a single ring
 * spinning is the thing every framework ships, and the eye has stopped seeing
 * it. The halo is what ties it to the rest of the product, because it is the
 * same accent wash the cards and chips use.
 *
 * RESTRAINT IS THE POINT. This appears while someone is waiting, often several
 * times in a flow. Anything with personality enough to notice twice is a thing
 * to resent by the fifth time, so: no bouncing, no colour cycling, no mascot.
 * Two speeds and a glow, and it gets out of the way.
 *
 * REDUCED MOTION is honoured completely — the arcs stop and the mark becomes a
 * static ring. A spinner is decoration around a message; the message is in the
 * live region beside it, so stillness costs nothing.
 */
import React from 'react';
import { Box } from '@mui/material';

/**
 * @param size   outer diameter in px
 * @param tone   'accent' (default) or 'ink' for a quieter context
 */
export default function BrandSpinner({ size = 44, tone = 'accent' }) {
    const fg = tone === 'ink' ? 'var(--ink-3)' : 'var(--accent)';
    const ring = Math.max(2, Math.round(size / 16));

    return (
        <Box
            aria-hidden
            sx={{
                position: 'relative',
                width: size,
                height: size,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                '@keyframes xeoSpin': { to: { transform: 'rotate(360deg)' } },
                '@keyframes xeoSpinBack': { to: { transform: 'rotate(-360deg)' } },
                '@keyframes xeoBreathe': {
                    '0%, 100%': { opacity: 0.25, transform: 'scale(0.86)' },
                    '50%': { opacity: 0.5, transform: 'scale(1.06)' },
                },
                /*
                 * Every animation off, not slowed. A reduced-motion setting is a
                 * request to stop moving things, and a slower spin is still a
                 * moving thing.
                 */
                '@media (prefers-reduced-motion: reduce)': {
                    '& *': { animation: 'none !important' },
                },
            }}
        >
            {/* The halo — the accent wash the rest of the product is built on. */}
            <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                bgcolor: 'var(--tint-2)',
                animation: 'xeoBreathe 2.4s ease-in-out infinite',
            }} />

            {/* Outer arc — one gap, turning clockwise. */}
            <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: `${ring}px solid transparent`,
                borderTopColor: fg,
                borderRightColor: fg,
                animation: 'xeoSpin 1.1s cubic-bezier(.5,.1,.5,.9) infinite',
            }} />

            {/* Inner arc — smaller, slower, and the other way. */}
            <Box sx={{
                position: 'absolute', inset: `${Math.round(size * 0.26)}px`, borderRadius: '50%',
                border: `${Math.max(2, ring - 1)}px solid transparent`,
                borderBottomColor: fg,
                opacity: 0.55,
                animation: 'xeoSpinBack 1.7s linear infinite',
            }} />
        </Box>
    );
}
